import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Pencil, X, Save, History } from "lucide-react";
import { toast } from "sonner";
import ImageUpload, { resolveImageUrl } from "../../components/admin/ImageUpload";

/**
 * Generic CRUD manager
 * resource = { key, label, route, fields: [{name, label, type, options?}] }
 *   field types: text | textarea | boolean | list | number | image | status
 */
export default function ResourceManager({ resource }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/${resource.route}?active_only=false`);
      setItems(data);
    } catch {
      toast.error("Could not load items.");
    }
  };

  useEffect(() => {
    load();
  }, [resource.route]); // eslint-disable-line react-hooks/exhaustive-deps

  const blankForm = () => {
    const empty = {};
    resource.fields.forEach((f) => {
      if (f.type === "list") empty[f.name] = [];
      else if (f.type === "boolean") empty[f.name] = true;
      else if (f.type === "number") empty[f.name] = 0;
      else if (f.type === "status") empty[f.name] = "draft";
      else empty[f.name] = "";
    });
    return empty;
  };

  const openNew = () => {
    setForm(blankForm());
    setEditing("new");
  };

  const openEdit = (item) => {
    const copy = {};
    resource.fields.forEach((f) => {
      if (f.type === "status") copy[f.name] = item[f.name] || "draft";
      else copy[f.name] = item[f.name] ?? (f.type === "list" ? [] : f.type === "boolean" ? true : f.type === "number" ? 0 : "");
    });
    setForm(copy);
    setEditing(item);
  };

  const close = () => {
    setEditing(null);
    setForm({});
  };

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      resource.fields.forEach((f) => {
        if (f.type === "list" && typeof payload[f.name] === "string") {
          payload[f.name] = payload[f.name].split(",").map((x) => x.trim()).filter(Boolean);
        }
        if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0);
      });
      if (editing === "new") {
        await api.post(`/admin/${resource.route}`, payload);
        toast.success(`${resource.label} created`);
      } else {
        await api.put(`/admin/${resource.route}/${editing.id}`, payload);
        toast.success(`${resource.label} updated`);
      }
      close();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setLoading(false);
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item[resource.titleField || "title"] || item.name}"?`)) return;
    try {
      await api.delete(`/admin/${resource.route}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openVersions = async (item) => {
    try {
      const { data } = await api.get(`/admin/versions/${resource.entity}/${item.id}`);
      setVersions({ item, items: data });
    } catch {
      toast.error("Could not load versions.");
    }
  };

  const restoreVersion = async (vId) => {
    if (!window.confirm("Restore this version? Current content will be saved as a new version first.")) return;
    try {
      await api.post(`/admin/versions/${vId}/restore`);
      toast.success("Version restored.");
      setVersions(null);
      load();
    } catch {
      toast.error("Restore failed.");
    }
  };

  const titleField = resource.titleField || "title";

  return (
    <div data-testid={`manager-${resource.key}`} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="section-overline">Content</div>
          <h1 className="font-display text-3xl font-semibold text-[#0A1128] mt-2">{resource.label}</h1>
        </div>
        <button
          onClick={openNew}
          data-testid={`${resource.key}-new`}
          className="inline-flex items-center gap-2 rounded-md bg-[#FF8C00] hover:bg-[#CC7000] text-white px-5 py-2.5 text-sm font-semibold"
        >
          <Plus size={16} /> New {resource.label}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {items.length === 0 && <div className="p-6 text-sm text-slate-500">No items yet.</div>}
        {items.map((item) => {
          const thumb = resolveImageUrl(item.image_url || item.cover_image || item.photo_url || "");
          const status = item.status || "published";
          return (
            <div key={item.id} className="flex items-center justify-between p-5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
              <div className="flex items-center gap-4 min-w-0">
                {thumb ? (
                  <img src={thumb} alt="" className="h-12 w-12 rounded-md object-cover shrink-0" />
                ) : (
                  <div className="h-12 w-12 rounded-md bg-slate-100 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-[#0A1128] truncate">{item[titleField] || item.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {item.category || item.designation || item.department || item.tagline || ""}
                    {item.version ? ` · v${item.version}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs px-2.5 py-1 rounded-md ${status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {status}
                </span>
                <button onClick={() => openVersions(item)} title="Versions" className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 hover:border-[#0A1128]">
                  <History size={14} />
                </button>
                <button onClick={() => openEdit(item)} data-testid={`${resource.key}-edit-${item.id}`} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 hover:border-[#FF8C00]">
                  <Pencil size={14} />
                </button>
                <button onClick={() => del(item)} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 hover:border-red-500 hover:text-red-500">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#050914]/70 backdrop-blur-sm flex items-center justify-center p-6">
          <div data-testid={`${resource.key}-form`} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 sticky top-0 bg-white z-10">
              <h2 className="font-display text-2xl font-semibold text-[#0A1128]">
                {editing === "new" ? `New ${resource.label}` : `Edit ${resource.label}`}
              </h2>
              <button onClick={close} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 hover:border-[#0A1128]">
                <X size={16} />
              </button>
            </div>
            <div className="p-6 grid md:grid-cols-2 gap-5">
              {resource.fields.map((f) => (
                <FormField
                  key={f.name}
                  field={f}
                  value={form[f.name]}
                  onChange={(v) => setForm({ ...form, [f.name]: v })}
                />
              ))}
            </div>
            <div className="p-6 border-t border-slate-100 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={close} className="px-5 py-2.5 rounded-md border border-slate-200 text-sm font-semibold">Cancel</button>
              <button onClick={save} disabled={loading} data-testid={`${resource.key}-save`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0A1128] text-white text-sm font-semibold disabled:opacity-60">
                <Save size={14} /> {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {versions && (
        <div className="fixed inset-0 z-50 bg-[#050914]/70 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <div className="section-overline">Version history</div>
                <h2 className="font-display text-xl font-semibold text-[#0A1128] mt-2">{versions.item[titleField] || versions.item.name}</h2>
              </div>
              <button onClick={() => setVersions(null)} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">Close</button>
            </div>
            <div>
              {versions.items.length === 0 && <div className="p-6 text-sm text-slate-500">No prior versions yet.</div>}
              {versions.items.map((v) => (
                <div key={v.id} className="p-5 border-b border-slate-100 last:border-b-0 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[#0A1128]">v{v.version}</div>
                    <div className="text-xs text-slate-500">{v.saved_at} · by {v.saved_by}</div>
                  </div>
                  <button onClick={() => restoreVersion(v.id)} className="inline-flex items-center gap-2 rounded-md bg-[#0A1128] hover:bg-[#15244B] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wide">
                    Restore
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FormField = ({ field, value, onChange }) => {
  const base = "w-full px-4 py-2.5 rounded-md border border-slate-200 focus:border-[#FF8C00] outline-none text-[#0A1128] text-sm";
  const wide = ["description", "body", "bio", "summary", "problem", "solution", "impact", "message", "excerpt"].includes(field.name);
  if (field.type === "image") {
    return (
      <div className="md:col-span-2">
        <ImageUpload label={field.label} value={value} onChange={onChange} testid={`upload-${field.name}`} />
      </div>
    );
  }
  return (
    <div className={wide || field.type === "status" ? "md:col-span-2" : ""}>
      <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{field.label}</label>
      <div className="mt-1.5">
        {field.type === "textarea" ? (
          <textarea rows={5} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base + " resize-none"} />
        ) : field.type === "boolean" ? (
          <label className="inline-flex items-center gap-2 text-sm text-[#0A1128]">
            <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#FF8C00]" />
            <span>{value ? "Active" : "Inactive"}</span>
          </label>
        ) : field.type === "list" ? (
          <input
            value={Array.isArray(value) ? value.join(", ") : value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Comma-separated values"
            className={base}
          />
        ) : field.type === "number" ? (
          <input type="number" value={value ?? 0} onChange={(e) => onChange(e.target.value)} className={base} />
        ) : field.type === "status" ? (
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            {["draft", "published"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onChange(s)}
                data-testid={`status-${s}`}
                className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider ${value === s ? "bg-[#0A1128] text-white" : "text-slate-600 hover:bg-slate-50"}`}
              >
                {s}
              </button>
            ))}
          </div>
        ) : (
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />
        )}
      </div>
    </div>
  );
};
